/**
 * webrtcConfig.ts
 *
 * Centralized WebRTC ICE server configuration for ALL otto-ui components.
 * Fetches from the hub backend (which proxies cloud-managed TURN credentials
 * from otto-web when available). Falls back to Google public STUN on failure.
 *
 * === USAGE ===
 *   import { getIceServers } from '@/services/webrtcConfig';
 *   const iceServers = await getIceServers();
 *   const pc = new RTCPeerConnection({ iceServers });
 *
 * DO NOT hardcode ICE servers in components — always use getIceServers().
 */

import { apiFetch } from './api';

/** Google public STUN — last-resort fallback when the backend is unreachable. */
export const DEFAULT_STUN_ICE_SERVERS: RTCIceServer[] = [{ urls: 'stun:stun.l.google.com:19302' }];

interface IceServersResponse {
  iceServers: RTCIceServer[];
  turnExpiresAt?: string;
  turnMode?: string;
}

let cachedConfig: RTCIceServer[] | null = null;
let cachedExpiresAt: number | null = null;
let fetchPromise: Promise<RTCIceServer[]> | null = null;

/** Refresh 5 minutes before TURN credentials expire. */
const REFRESH_BEFORE_EXPIRY_MS = 5 * 60 * 1000;

/**
 * Get ICE servers from the backend, with expiry-aware caching and fallback.
 * Returns cached config if still valid, otherwise fetches fresh credentials.
 */
export async function getIceServers(): Promise<RTCIceServer[]> {
  // Refresh if cache is about to expire
  if (cachedConfig && cachedExpiresAt) {
    if (Date.now() < cachedExpiresAt - REFRESH_BEFORE_EXPIRY_MS) {
      return cachedConfig;
    }
    // Expiring soon — clear and re-fetch
    cachedConfig = null;
    cachedExpiresAt = null;
  }

  if (cachedConfig) return cachedConfig;

  if (!fetchPromise) {
    fetchPromise = apiFetch<IceServersResponse>('/audio-zones/webrtc-config')
      .then((data) => {
        cachedConfig = data.iceServers || DEFAULT_STUN_ICE_SERVERS;
        if (data.turnExpiresAt) {
          cachedExpiresAt = new Date(data.turnExpiresAt).getTime();
        }
        return cachedConfig;
      })
      .catch(() => {
        // On failure, return cached if available, otherwise STUN-only fallback
        return cachedConfig || DEFAULT_STUN_ICE_SERVERS;
      })
      .finally(() => {
        fetchPromise = null;
      });
  }

  return fetchPromise;
}

/**
 * Synchronous fallback for edge cases where async isn't possible.
 * Returns cached config or STUN-only. Prefer getIceServers().
 */
export function getIceServersSync(): RTCIceServer[] {
  return cachedConfig || DEFAULT_STUN_ICE_SERVERS;
}

/**
 * Clear the cached config (call on logout or config change).
 */
export function clearWebRTCConfigCache(): void {
  cachedConfig = null;
  cachedExpiresAt = null;
}
